/* eslint-disable react-refresh/only-export-components */

import { useAppDispatch } from "@/redux/dispatch";
import { resetIncidentState } from "@/redux/slice/incidentSlice";
import { setBaseLocation } from "@/redux/slice/locationSlice";
import api from "@/services/api";
import { UserDetails, UserDetailsResponse } from "@/types/user";
import { toast } from "sonner";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  useRef,
  useMemo,
} from "react";
import keycloak from "@/config/keycloak";

interface AuthContextType {
  isAuthenticated: boolean;
  user: unknown;
  userDetails: UserDetails | null;
  loading: boolean;
  sessionInvalid: boolean;
  accessToken: string | null; // This will hold the ID Token
  login: () => Promise<void>;
  logout: () => Promise<void>;
  checkSession: () => Promise<void>;
  fetchUserDetails: () => Promise<void>;
  setAccessToken?: (token: string | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const TOKEN_KEY = "auth_id_token";

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<unknown>(null);
  const [userDetails, setUserDetails] = useState<UserDetails | null>(() => {
    const storedUserDetails = localStorage.getItem("userDetails");
    return storedUserDetails ? JSON.parse(storedUserDetails) : null;
  });
  const [loading, setLoading] = useState(true);
  const [accessToken, setAccessToken] = useState<string | null>(() =>
    localStorage.getItem(TOKEN_KEY)
  );
  const [sessionInvalid, setSessionInvalid] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const dispatch = useAppDispatch();
  const initRef = useRef(false);

  const checkIsResponder = useCallback(() => {
    const urlParams = new URLSearchParams(globalThis.location.search);
    return !!urlParams.get("id") || globalThis.location.pathname.startsWith("/responder");
  }, []);

  const handleLogout = useCallback(async () => {
    // If responder, we should not attempt to logout via Keycloak or clear storage aggressively if not needed for them,
    // but usually responders don't have session anyway. Safest is to just ignore if it accidentally triggered.
    if (checkIsResponder()) return;

    console.log("AuthProvider: handleLogout called");
    try {
      localStorage.clear();
      await keycloak.logout({
        redirectUri: globalThis.location.origin + "/login",
      });
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      setUser(null);
      setAccessToken(null);
      setSessionInvalid(false);
      setAuthenticated(false);
      setUserDetails(null);
      dispatch(resetIncidentState());
      dispatch(setBaseLocation(""));
    }
  }, [dispatch, checkIsResponder]);

  const fetchUserDetails = useCallback(async () => {
    if (checkIsResponder()) return;
    try {
      console.log("AuthProvider: fetching user details...");
      const { data } = await api.get<UserDetailsResponse>("/console-teams/me");
      const fetchedUserDetails = data.data;
      setUserDetails(fetchedUserDetails);
      localStorage.setItem("userDetails", JSON.stringify(fetchedUserDetails));
      console.log("AuthProvider: user details fetched successfully");
    } catch (error: any) {
      console.error("Failed to fetch user details:", error);
      if (error.response?.status === 401) {
        toast.error("User is Unauthorised");
        handleLogout();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleLogout]);

  const checkSession = useCallback(async () => {
    if (checkIsResponder()) return;
    if (keycloak.authenticated) {
      try {
        const refreshed = await keycloak.updateToken(5);
        if (refreshed || !accessToken) {
          const token = keycloak.idToken;
          if (token) {
            localStorage.setItem(TOKEN_KEY, token);
            setAccessToken(token);
            setSessionInvalid(false);
            setAuthenticated(true);
          }
        }
      } catch (error) {
        console.error("Session check failed:", error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, handleLogout]);

  useEffect(() => {
    const interceptor = api.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401 || error.response?.status === 403) {
          if (!checkIsResponder()) {
            console.error("Session invalid context:", error);
            toast.error("Session expired. Please login again.");
            handleLogout();
          }
        }
        throw error;
      }
    );

    return () => {
      api.interceptors.response.eject(interceptor);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleLogout]);

  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    const initKeycloak = async () => {
      const isResponder = checkIsResponder();

      console.log("AuthProvider: Initializing Keycloak...", { isResponder });
      try {
        const auth = await keycloak.init({
          onLoad: "check-sso",
          silentCheckSsoRedirectUri: globalThis.location.origin + "/silent-check-sso.html",
          pkceMethod: "S256",
          checkLoginIframe: false,
        });

        console.log("AuthProvider: Keycloak init result:", auth);

        // Only set admin auth state if not responder
        if (!isResponder) {
          if (auth) {
            setAuthenticated(true);
            setUser(keycloak.tokenParsed);
            setAccessToken(keycloak.idToken || null);
            if (keycloak.idToken) {
              localStorage.setItem(TOKEN_KEY, keycloak.idToken);
            }
            await fetchUserDetails();
          } else {
            setAuthenticated(false);
            setAccessToken(null);
            localStorage.removeItem(TOKEN_KEY);
          }
        }
      } catch (error) {
        console.error("AuthProvider: Keycloak initialization failed:", error);
        if (!isResponder) {
          setAuthenticated(false);
        }
      } finally {
        setLoading(false);
      }
    };

    initKeycloak();

    const interval = setInterval(() => {
      // Only refresh tokens for admin users, not responders
      if (!checkIsResponder() && keycloak.authenticated) {
        keycloak.updateToken(70).then((refreshed) => {
          if (refreshed) {
            console.log("AuthProvider: Token refreshed (ID Token)");
            setAccessToken(keycloak.idToken || null);
            if (keycloak.idToken) {
              localStorage.setItem(TOKEN_KEY, keycloak.idToken);
            }
          }
        }).catch(() => {
          console.error("AuthProvider: Failed to refresh token");
        });
      }
    }, 60000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchUserDetails, handleLogout]);

  const login = useCallback(async () => {
    console.log("AuthProvider: login called");
    await keycloak.login();
  }, []);

  const authState = useMemo(() => ({
    isAuthenticated: authenticated && !!accessToken && !sessionInvalid,
    user,
    loading,
    sessionInvalid,
    accessToken,
    login,
    logout: handleLogout,
    checkSession,
    userDetails,
    fetchUserDetails,
    setAccessToken,
  }), [authenticated, accessToken, sessionInvalid, user, loading, login, handleLogout, checkSession, userDetails, fetchUserDetails]);

  return (
    <AuthContext.Provider value={authState}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
